/**
 * Status language — every status string in the app maps to ONE tone, everywhere.
 *
 *   green   Active / OK / Ready / In stock
 *   amber   Pending / Submitted / On hold / Waiting / Low / Revise & resubmit
 *   red     Blocked / Overdue / Rejected / Missing / Ran out
 *   blue    In progress / Open / Partially delivered / Booked
 *   navy    Completed / Approved / Done / Paid / Delivered
 *   gray    Not started / Draft / Not booked / Never stocked
 *   violet  Variation / VO
 *   teal    Approved with comments / Completed with remarks
 *   lime    New (feature / record marker)
 *   outline Not required / Cancelled / Superseded / For record
 *
 * A `domain` adds the few meanings that differ by context (e.g. a shop-drawing
 * "PENDING" is work in progress → blue; a BOQ line "Delivered" is good news → green).
 * Use <StatusPill status domain /> to render; use getStatusMeta() for logic.
 *
 * Labels — one rule, so the same status reads the same in every domain:
 * - Text a person or the backend wrote ('Payment Received', 'Low stock', 'Pending from sales')
 *   is shown exactly as stored; only its tone is looked up.
 * - Machine values (NOT_STARTED, in_progress, GRN, A…D) get a label: the domain's label if it has
 *   one (e.g. movement 'issue' → 'Site DN'), else the canonical spelling of the core workflow
 *   statuses (Not Started, In Progress, On Hold … — the backend's own Title Case), else sentence
 *   case ('MISSING_DESIGN_INFO' → 'Missing design info'; acronyms kept: 'NO_DRAWING_ID' → 'No drawing ID').
 * See docs/UI_FOUNDATION.md.
 */

export const STATUS_TONES = ['green', 'amber', 'red', 'blue', 'navy', 'gray', 'violet', 'teal', 'lime', 'outline'];

/** Literal class names (kept literal so Tailwind keeps the @layer rules in index.css). */
export const TONE_CLASS = {
  green: 'tone-green',
  amber: 'tone-amber',
  red: 'tone-red',
  blue: 'tone-blue',
  navy: 'tone-navy',
  gray: 'tone-gray',
  violet: 'tone-violet',
  teal: 'tone-teal',
  lime: 'tone-lime',
  outline: 'tone-outline',
};

export const toneClass = (tone) => TONE_CLASS[tone] || TONE_CLASS.gray;

/** 'Payment Received' → 'payment_received'; 'Revise & resubmit' → 'revise_and_resubmit'. */
export function normalizeStatusKey(status) {
  if (status === null || status === undefined) return '';
  return String(status)
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\bw\//g, 'with ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

const ALIASES = {
  canceled: 'cancelled',
  inprogress: 'in_progress',
  wip: 'in_progress',
  onhold: 'on_hold',
  hold: 'on_hold',
  paused: 'on_hold',
  complete: 'completed',
  approved_with_comment: 'approved_with_comments',
  completed_with_remark: 'completed_with_remarks',
  revise_resubmit: 'revise_and_resubmit',
  out_of_stock: 'ran_out',
  low_stock: 'low',
  n_a: 'not_applicable',
  na: 'not_applicable',
  bottlenecked: 'bottleneck',
  waiting_approval: 'waiting_for_approval',
  awaiting_approval: 'waiting_for_approval',
  pending_approval: 'waiting_for_approval',
  partially_paid: 'partial',
};

/* Base vocabulary: normalized key → tone. */
const BASE_TONES = {
  // green
  active: 'green',
  ok: 'green',
  ready: 'green',
  in_stock: 'green',
  available: 'green',
  connected: 'green',
  restorable: 'green',
  synced: 'green',
  enabled: 'green',
  healthy: 'green',
  on_track: 'green',
  task_created: 'green',
  fulfilled: 'green',
  // amber
  pending: 'amber',
  submitted: 'amber',
  on_hold: 'amber',
  waiting: 'amber',
  waiting_for_approval: 'amber',
  awaiting: 'amber',
  awaiting_reply: 'amber',
  awaiting_consultant: 'amber',
  update_required: 'amber',
  needs_review: 'amber',
  proposed: 'amber',
  low: 'amber',
  below_min_level: 'amber',
  partial: 'amber',
  // Needs action from us, but not a final verdict — consultant code C. D / Rejected is red.
  revise_and_resubmit: 'amber',
  empty: 'amber',
  setup_needed: 'amber',
  test_account: 'amber',
  unlinked: 'amber',
  due_soon: 'amber',
  due_today: 'amber',
  reason_missing: 'amber',
  // red
  blocked: 'red',
  bottleneck: 'red',
  overdue: 'red',
  late: 'red',
  rejected: 'red',
  missing: 'red',
  ran_out: 'red',
  failed: 'red',
  error: 'red',
  expired: 'red',
  duplicate: 'red',
  short: 'red',
  // blue
  in_progress: 'blue',
  open: 'blue',
  partially_delivered: 'blue',
  booked: 'blue',
  reserved: 'blue',
  processing: 'blue',
  scheduled: 'blue',
  in_transit: 'blue',
  // navy
  completed: 'navy',
  approved: 'navy',
  approved_as_noted: 'navy',
  done: 'navy',
  paid: 'navy',
  payment_received: 'navy',
  delivered: 'navy',
  received: 'navy',
  closed: 'navy',
  resolved: 'navy',
  consumed: 'navy',
  // gray
  not_started: 'gray',
  draft: 'gray',
  not_booked: 'gray',
  never_stocked: 'gray',
  not_delivered: 'gray',
  not_set: 'gray',
  not_linked: 'gray',
  not_synced: 'gray',
  default: 'gray',
  inactive: 'gray',
  disabled: 'gray',
  unassigned: 'gray',
  released: 'gray',
  none: 'gray',
  // violet
  variation: 'violet',
  vo: 'violet',
  // teal
  approved_with_comments: 'teal',
  completed_with_remarks: 'teal',
  // lime
  new: 'lime',
  // outline
  not_required: 'outline',
  not_applicable: 'outline',
  cancelled: 'outline',
  superseded: 'outline',
  for_record: 'outline',
  not_recorded: 'outline',
  archived: 'outline',
  undone: 'outline',
};

/* Prefix rules for free-text statuses ("Pending from sales", "Handover overdue · 48 d"). */
const PREFIX_TONES = [
  ['pending_', 'amber'],
  ['awaiting_', 'amber'],
  ['waiting_', 'amber'],
  ['due_', 'amber'],
  ['blocked_', 'red'],
  ['overdue_', 'red'],
  ['late_', 'red'],
  ['missing_', 'red'],
  ['rejected_', 'red'],
  ['handover_overdue', 'red'],
  ['variation_', 'violet'],
  ['vo_', 'violet'],
];

/* Context-specific meanings. Keys are domain names you pass to StatusPill/getStatusMeta. */
export const STATUS_DOMAINS = {
  /** Projects: Active / On Hold / Completed. The stored value is the fixed enum 'On Hold' (not free text), so its
      label applies to that text too (`textLabels`): "On hold", as the Projects tabs and the approved design say it. */
  project: { labels: { on_hold: 'On hold' }, textLabels: true },
  /** Delivery stages: Not Started / In Progress / Submitted / Approved / Rejected / Bottlenecked */
  stage: {},
  /** Stage line items: Pending from presales|sales|Execution|contractor / Completed */
  stage_line: {},
  /** Execution tasks: Not Started / In Progress / Done / Bottleneck / Not required / Update required / Waiting for Approval */
  execution: {},
  /** Shop drawings — all 12 backend statuses */
  shop_drawing: {
    tones: { pending: 'blue' },
    icons: { on_hold: 'pause' },
    labels: { on_hold: 'On hold' },
    // Labels come from CANONICAL_LABELS / sentence case: 'In Progress', 'Not Started', 'On Hold',
    // 'Completed w/ remarks', 'Approved w/ comments' — the spelling the page already used.
  },
  /** Consultant reply codes on shop-drawing submissions (A/B/C/D) */
  consultant_reply: {
    tones: { a: 'navy', b: 'teal', c: 'amber', d: 'red', revise_and_resubmit: 'amber', pending: 'amber' },
    labels: {
      a: 'A · Approved',
      b: 'B · Approved w/ comments',
      c: 'C · Revise & resubmit',
      d: 'D · Rejected',
      pending: 'Awaiting reply',
      approved_with_comments: 'Approved w/ comments',
    },
  },
  /** Client invoices: Not Started / Submitted / Payment Received */
  invoice: {},
  /** Variations (VOs): Proposed / Approved / Rejected */
  variation: {},
  /** LPO delivery state */
  lpo: { labels: { active: 'Active' } },
  /** Site requests: Open / Fulfilled / Cancelled */
  site_request: {},
  /** Tasks: Open / In Progress / Blocked / Done / Cancelled */
  task: { tones: { open: 'gray' } },
  /** Task priority: High / Medium / Low */
  priority: { tones: { urgent: 'red', high: 'red', medium: 'amber', low: 'gray' } },
  /**
   * Inventory stock state. Machine values get the approved inventory wording (in_stock → 'In stock',
   * low / low_stock → 'Low', ran_out / out_of_stock → 'Ran out', never_stocked → 'Never stocked');
   * human text such as 'Low stock' or 'Out of stock' keeps its words and only takes the tone.
   */
  stock: { labels: { ran_out: 'Ran out', low: 'Low' } },
  /** Site BOQ lines (booking / delivery progress) */
  boq: { tones: { delivered: 'green', partial: 'amber' } },
  /** Stock movement types (backend values: GRN, transfer, issue, testing, return, adjustment) */
  movement: {
    tones: {
      grn: 'green',
      transfer: 'blue',
      issue: 'violet',
      site_dn: 'violet',
      testing: 'teal',
      lab_use: 'teal',
      return: 'navy',
      adjustment: 'gray',
    },
    labels: {
      grn: 'GRN',
      transfer: 'Transfer',
      issue: 'Site DN',
      site_dn: 'Site DN',
      testing: 'Lab use',
      lab_use: 'Lab use',
      return: 'Return',
      adjustment: 'Adjustment',
    },
  },
  /** Project folder health: Ready / Empty / Missing */
  folder: {},
  /** Documents: Submitted / Approved / Open / Revise & resubmit / Draft / For record / Superseded / Unlinked */
  document: { icons: { task_created: 'check' } },
  /** Integrations / admin: Connected / Setup needed / Not linked */
  integration: {},
  /** History / undo entries: available / undone / canceled / superseded */
  history: { tones: { available: 'green' }, labels: { available: 'Restorable', undone: 'Undone', cancelled: 'Cancelled' } },
};

const UPPER_LABELS = {
  grn: 'GRN',
  lpo: 'LPO',
  vo: 'VO',
  boq: 'BOQ',
  dn: 'DN',
  ok: 'OK',
  id: 'ID',
  po: 'PO',
  rfi: 'RFI',
  pdf: 'PDF',
  dwg: 'DWG',
  ai: 'AI',
  n_a: 'N/A',
};

/*
  Canonical wording for machine values (UPPER_SNAKE / snake_case), by normalized key. The core
  workflow statuses use the backend's Title Case so a shop drawing's IN_PROGRESS and a stage's
  'In Progress' read the same; everything else is sentence case.
*/
const CANONICAL_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  waiting_for_approval: 'Waiting for Approval',
  payment_received: 'Payment Received',
  approved_with_comments: 'Approved w/ comments',
  completed_with_remarks: 'Completed w/ remarks',
  revise_and_resubmit: 'Revise & resubmit',
  in_stock: 'In stock',
  never_stocked: 'Never stocked',
  n_a: 'N/A',
};

/** Machine values: UPPER_SNAKE, snake_case, or a single bare code ('A', 'grn'). */
export const isMachineStatus = (text) => /^[A-Z0-9_]+$/.test(text) || /^[a-z0-9_]+$/.test(text);

/** 'NOT_STARTED' → 'Not Started'; 'MISSING_DESIGN_INFO' → 'Missing design info'; human text is kept as written. */
export function humanizeStatus(status) {
  if (status === null || status === undefined) return '';
  const text = String(status).trim();
  if (!text) return '';
  if (!isMachineStatus(text)) return text;
  const key = text.toLowerCase();
  if (CANONICAL_LABELS[key]) return CANONICAL_LABELS[key];
  const aliased = ALIASES[key];
  if (aliased && CANONICAL_LABELS[aliased]) return CANONICAL_LABELS[aliased];
  const words = key.split('_').filter(Boolean)
    .map((word) => UPPER_LABELS[word] || word);
  if (!words.length) return '';
  words[0] = words[0] === words[0].toUpperCase() ? words[0] : words[0][0].toUpperCase() + words[0].slice(1);
  return words.join(' ');
}

/**
 * getStatusMeta('Payment Received', 'invoice') →
 *   { key: 'payment_received', tone: 'navy', label: 'Payment Received', icon: null, known: true }
 * Unknown statuses fall back to gray with their own text as the label (known: false).
 */
export function getStatusMeta(status, domain) {
  const rawKey = normalizeStatusKey(status);
  const key = ALIASES[rawKey] || rawKey;
  const domainSpec = (domain && STATUS_DOMAINS[domain]) || {};
  let tone = domainSpec.tones?.[key] || domainSpec.tones?.[rawKey] || BASE_TONES[key];
  let known = Boolean(tone);
  if (!tone && key) {
    const match = PREFIX_TONES.find(([prefix]) => key.startsWith(prefix));
    if (match) {
      [, tone] = match;
      known = true;
    }
  }
  // Domain labels rename machine values only; text someone wrote is never reworded — except in a domain whose values
  // are a fixed enum stored as words (`textLabels`, e.g. project 'On Hold').
  const text = status === null || status === undefined ? '' : String(status).trim();
  const domainLabel = text && (isMachineStatus(text) || domainSpec.textLabels)
    ? domainSpec.labels?.[rawKey] || domainSpec.labels?.[key]
    : undefined;
  const label = domainLabel || humanizeStatus(status);
  return {
    key,
    tone: tone || 'gray',
    label,
    icon: domainSpec.icons?.[key] || null,
    known,
  };
}

export const getStatusTone = (status, domain) => getStatusMeta(status, domain).tone;
export const getStatusLabel = (status, domain) => getStatusMeta(status, domain).label;

/**
 * Age → attention tone, for '86 d' style cells.
 * Default thresholds (shop drawings, supply): ≥ 91 d red, 31–90 d amber, else null.
 * Documents use { amberFrom: 120, redFrom: 181 }.
 */
export function ageTone(days, { amberFrom = 31, redFrom = 91 } = {}) {
  if (days === null || days === undefined || Number.isNaN(Number(days))) return null;
  const value = Number(days);
  if (value >= redFrom) return 'red';
  if (value >= amberFrom) return 'amber';
  return null;
}

/** Text colour class for a tone (for ages, attention numbers, legends). */
export const TONE_TEXT_CLASS = {
  green: 'text-status-green-ink',
  amber: 'text-status-amber-ink',
  red: 'text-status-red-ink',
  blue: 'text-status-blue-ink',
  navy: 'text-status-navy-ink',
  gray: 'text-ink-3',
  violet: 'text-status-violet-ink',
  teal: 'text-status-teal-ink',
  lime: 'text-status-lime-ink',
  outline: 'text-ink-2',
};
